/*
 * SAITECH FEATURE:
 * Larger-quantity request form + EmailJS
 *
 * EmailJS variables sent:
 *
 * customer_name
 * customer_email
 * reply_to
 * phone
 * product_name
 * product_id
 * product_url
 * variant_id
 * variant_title
 * sku
 * purchase_limit
 * requested_quantity
 * page_url
 * submitted_at
 * store_name
 * to_email
 * message
 * subject
 */

(function () {
  'use strict';

  var SDK_URL =
    'https://cdn.jsdelivr.net/npm/@emailjs/browser@4/dist/email.min.js';

  var api =
    window.SaitechQuantityRequestForm ||
    {};

  var lastFocusedElement = null;
  var sdkPromise = null;
  var submittedSuccessfully = false;


  function root() {
    return document.querySelector(
      '[data-saitech-quantity-limit-root]'
    );
  }


  function modal() {
    var currentRoot = root();

    return currentRoot
      ? currentRoot.querySelector(
          '[data-saitech-quantity-request-modal]'
        )
      : null;
  }


  function form() {
    var currentModal = modal();

    return currentModal
      ? currentModal.querySelector(
          '[data-saitech-quantity-request-form]'
        )
      : null;
  }


  /*
   * Get EmailJS settings from:
   *
   * Shopify Theme Editor
   * -> Selected qty limit
   */
  function emailConfig() {
    var currentRoot = root();

    var configElement =
      currentRoot
        ? currentRoot.querySelector(
            '#SaitechQuantityLimitConfig'
          )
        : null;

    if (!configElement) {
      return {};
    }

    try {
      var config =
        JSON.parse(
          configElement.textContent
        );

      var emailjs =
        config.emailjs || {};

      return {
        publicKey:
          String(
            emailjs.publicKey || ''
          ).trim(),

        serviceId:
          String(
            emailjs.serviceId || ''
          ).trim(),

        templateId:
          String(
            emailjs.templateId || ''
          ).trim(),

        recipientEmail:
          String(
            emailjs.recipientEmail || ''
          ).trim(),

        storeName:
          String(
            emailjs.storeName || 'eSaitech'
          ).trim()
      };
    } catch (error) {
      console.error(
        '[Saitech quantity request] Invalid config.',
        error
      );

      return {};
    }
  }


  /*
   * Load EmailJS browser library.
   */
  function loadEmailJs() {
    if (
      window.emailjs &&
      typeof window.emailjs.send ===
        'function'
    ) {
      return Promise.resolve(
        window.emailjs
      );
    }

    if (sdkPromise) {
      return sdkPromise;
    }


    sdkPromise =
      new Promise(
        function (
          resolve,
          reject
        ) {

          var existing =
            document.querySelector(
              'script[src*="@emailjs/browser"]'
            );

          var script =
            existing ||
            document.createElement(
              'script'
            );

          var done = false;


          function finish() {
            if (done) {
              return;
            }

            done = true;

            if (
              window.emailjs &&
              typeof window.emailjs.send ===
                'function'
            ) {
              resolve(
                window.emailjs
              );
            } else {
              reject(
                new Error(
                  'EmailJS SDK is unavailable.'
                )
              );
            }
          }


          function fail() {
            if (done) {
              return;
            }

            done = true;

            reject(
              new Error(
                'Unable to load EmailJS SDK.'
              )
            );
          }


          if (existing) {

            existing.addEventListener(
              'load',
              finish,
              {
                once: true
              }
            );

            existing.addEventListener(
              'error',
              fail,
              {
                once: true
              }
            );

            window.setTimeout(
              finish,
              2500
            );

            return;
          }


          script.src =
            SDK_URL;

          script.async =
            true;

          script.defer =
            true;


          script.addEventListener(
            'load',
            finish,
            {
              once: true
            }
          );


          script.addEventListener(
            'error',
            fail,
            {
              once: true
            }
          );


          document.head.appendChild(
            script
          );
        }
      ).catch(
        function (error) {
          sdkPromise = null;
          throw error;
        }
      );


    return sdkPromise;
  }


  /*
   * Set form field value.
   */
  function setField(
    name,
    value
  ) {
    var currentForm =
      form();

    var field =
      currentForm
        ? currentForm.elements.namedItem(
            name
          )
        : null;

    if (field) {
      field.value =
        value == null
          ? ''
          : String(value);
    }
  }


  /*
   * Success / Error message.
   */
  function setStatus(
    type,
    message
  ) {
    var currentModal =
      modal();

    if (!currentModal) {
      return;
    }

    var success =
      currentModal.querySelector(
        '[data-saitech-quantity-request-success]'
      );

    var error =
      currentModal.querySelector(
        '[data-saitech-quantity-request-error]'
      );


    if (success) {
      success.hidden =
        type !== 'success';

      if (
        type === 'success'
      ) {
        success.textContent =
          message ||
          'Thank you. Your request has been sent.';
      }
    }


    if (error) {
      error.hidden =
        type !== 'error';

      error.textContent =
        type === 'error'
          ? message ||
            'Unable to send the request.'
          : '';
    }
  }


  /*
   * Disable button while sending.
   */
  function setSubmitting(
    isSubmitting
  ) {
    var currentModal =
      modal();

    var button =
      currentModal
        ? currentModal.querySelector(
            '[data-saitech-quantity-request-submit]'
          )
        : null;

    if (!button) {
      return;
    }

    button.disabled =
      Boolean(isSubmitting);

    button.textContent =
      isSubmitting
        ? 'Sending Request...'
        : 'Submit Request';
  }


  /*
   * Get a form field value.
   */
  function value(name) {
    var currentForm =
      form();

    var field =
      currentForm
        ? currentForm.elements.namedItem(
            name
          )
        : null;

    return (
      field &&
      typeof field.value ===
        'string'
    )
      ? field.value.trim()
      : '';
  }


  /*
   * Validate form.
   */
  function validate() {
    var currentForm =
      form();

    if (!currentForm) {
      return false;
    }


    /*
     * Clear old invalid markers.
     */
    Array.prototype.forEach.call(
      currentForm.querySelectorAll(
        '[aria-invalid="true"]'
      ),
      function (field) {
        field.removeAttribute(
          'aria-invalid'
        );
      }
    );


    /*
     * Browser validation:
     *
     * required
     * email
     * number
     * etc.
     */
    if (
      !currentForm.checkValidity()
    ) {

      var invalid =
        currentForm.querySelector(
          ':invalid'
        );

      if (invalid) {
        invalid.setAttribute(
          'aria-invalid',
          'true'
        );

        invalid.focus();
      }


      currentForm.reportValidity();

      return false;
    }


    /*
     * Requested quantity must be
     * HIGHER than website limit.
     */
    var quantityField =
      currentForm.elements.namedItem(
        'requested_quantity'
      );

    var requested =
      parseInt(
        quantityField.value,
        10
      );

    var limit =
      parseInt(
        quantityField.dataset
          .quantityLimit ||
          '0',
        10
      ) || 0;


    if (
      !Number.isFinite(
        requested
      ) ||
      requested <= limit
    ) {

      quantityField.setAttribute(
        'aria-invalid',
        'true'
      );

      quantityField.focus();


      setStatus(
        'error',
        'Requested quantity must be greater than the website purchase limit of ' +
          limit +
          '.'
      );


      return false;
    }


    return true;
  }


  /*
   * Called automatically from
   * saitech-quantity-limit.js
   *
   * when customer clicks:
   *
   * Request More Quantity
   */
  function open(data) {
    var currentModal =
      modal();

    var currentForm =
      form();

    if (
      !currentModal ||
      !currentForm
    ) {
      return;
    }


    data =
      data || {};

    lastFocusedElement =
      document.activeElement;

    submittedSuccessfully =
      false;


    setStatus(
      '',
      ''
    );

    setSubmitting(
      false
    );


    /*
     * AUTOMATIC PRODUCT / REQUEST CONTEXT
     */
    setField(
      'product_name',
      data.productTitle ||
        'Selected product'
    );

    setField(
      'product_id',
      data.productId || ''
    );

    setField(
      'product_url',
      data.productUrl ||
        window.location.href
    );

    setField(
      'variant_id',
      data.variantId || ''
    );

    setField(
      'variant_title',
      data.variantTitle || ''
    );

    setField(
      'sku',
      data.sku || ''
    );

    setField(
      'purchase_limit',
      Number(data.limit || 0)
    );

    setField(
      'page_url',
      window.location.href
    );


    /*
     * Automatically use attempted
     * quantity as requested quantity.
     *
     * Example:
     *
     * limit = 2
     * customer tries 5
     * requested quantity = 5
     */
    setField(
      'requested_quantity',
      data.requestedQuantity ||
        Number(
          data.limit || 0
        ) +
          1
    );


    var quantityField =
      currentForm.elements.namedItem(
        'requested_quantity'
      );


    var minimum =
      Math.max(
        1,
        Number(
          data.limit || 0
        ) + 1
      );


    quantityField.min =
      String(minimum);


    /*
     * Store limit internally.
     *
     * This is NOT another visible
     * form field.
     */
    quantityField.dataset.quantityLimit =
      String(
        Number(
          data.limit || 0
        )
      );


    /*
     * Example message:
     *
     * The regular website purchase
     * limit for this product is 2.
     */
    var note =
      currentModal.querySelector(
        '[data-saitech-quantity-request-limit-note]'
      );


    if (note) {
      note.textContent =
        data.limit
          ? 'The regular website purchase limit for this product is ' +
            data.limit +
            '.'
          : '';
    }


    /*
     * Open modal.
     */
    currentModal.hidden =
      false;


    currentModal.setAttribute(
      'aria-hidden',
      'false'
    );


    document.documentElement.classList.add(
      'saitech-quantity-request-is-open'
    );


    /*
     * Focus customer name.
     */
    window.setTimeout(
      function () {
        var first =
          currentForm.querySelector(
            '[name="customer_name"]'
          );

        if (first) {
          first.focus();
        }
      },
      50
    );
  }


  /*
   * Close popup.
   */
  function close() {
    var currentModal =
      modal();

    if (!currentModal) {
      return;
    }


    currentModal.hidden =
      true;


    currentModal.setAttribute(
      'aria-hidden',
      'true'
    );


    document.documentElement.classList.remove(
      'saitech-quantity-request-is-open'
    );


    setStatus(
      '',
      ''
    );


    setSubmitting(
      false
    );


    /*
     * Reset form after successful
     * submission.
     */
    var currentForm =
      form();


    if (
      submittedSuccessfully &&
      currentForm
    ) {
      currentForm.reset();

      submittedSuccessfully =
        false;
    }


    if (
      lastFocusedElement &&
      typeof lastFocusedElement.focus ===
        'function'
    ) {
      lastFocusedElement.focus();
    }
  }


  /*
   * These are the ONLY customer
   * information fields sent to EmailJS.
   */
  function parameters() {
    var config =
      emailConfig();

    var productName =
      value(
        'product_name'
      );

    var requestedQuantity =
      value(
        'requested_quantity'
      );

    var customerEmail =
      value(
        'customer_email'
      );

    var recipientEmail =
      config.recipientEmail ||
      'customerservice@esaitech.com';


    return {

      /* Customer */
      customer_name:
        value(
          'customer_name'
        ),

      customer_email:
        customerEmail,

      /* Use {{reply_to}} in EmailJS Reply-To. */
      reply_to:
        customerEmail,

      phone:
        value(
          'phone'
        ),


      /* Product / request */
      product_name:
        productName,

      product_id:
        value(
          'product_id'
        ),

      product_url:
        value(
          'product_url'
        ),

      variant_id:
        value(
          'variant_id'
        ),

      variant_title:
        value(
          'variant_title'
        ),

      sku:
        value(
          'sku'
        ),

      purchase_limit:
        value(
          'purchase_limit'
        ),

      requested_quantity:
        requestedQuantity,


      /* Submission context */
      page_url:
        value(
          'page_url'
        ) ||
        window.location.href,

      submitted_at:
        new Date().toISOString(),

      store_name:
        config.storeName ||
        'eSaitech',

      /* Use {{to_email}} in EmailJS To Email. */
      to_email:
        recipientEmail,


      /* Customer message */
      message:
        value(
          'message'
        ),


      subject:
        'Larger Quantity Request | ' +
        productName +
        ' | Qty ' +
        requestedQuantity
    };
  }


  /*
   * Submit to EmailJS.
   */
  function submit(event) {
    var currentForm =
      event.target;


    if (
      !currentForm ||
      !currentForm.matches(
        '[data-saitech-quantity-request-form]'
      )
    ) {
      return;
    }


    event.preventDefault();


    setStatus(
      '',
      ''
    );


    /*
     * Honeypot spam check.
     */
    var honeypot =
      currentForm.elements.namedItem(
        'website'
      );


    if (
      honeypot &&
      honeypot.value
    ) {
      return;
    }


    /*
     * Validate fields.
     */
    if (!validate()) {
      return;
    }


    /*
     * Read EmailJS configuration.
     */
    var config =
      emailConfig();


    if (
      !config.publicKey ||
      !config.serviceId ||
      !config.templateId
    ) {

      setStatus(
        'error',
        'The request form email service has not been configured yet. Please contact customerservice@esaitech.com.'
      );

      return;
    }


    setSubmitting(
      true
    );


    /*
     * Send email.
     */
    loadEmailJs()

      .then(
        function (emailjs) {

          return emailjs.send(

            config.serviceId,

            config.templateId,

            parameters(),

            {
              publicKey:
                config.publicKey
            }

          );
        }
      )

      .then(
        function () {

          submittedSuccessfully =
            true;


          setStatus(
            'success',
            'Thank you. Your larger-quantity request has been sent to Saitech.'
          );


          setSubmitting(
            false
          );

        }
      )

      .catch(
        function (error) {

          console.error(
            '[Saitech quantity request] EmailJS send failed.',
            error
          );


          setStatus(
            'error',
            'We could not send your request. Please try again or contact customerservice@esaitech.com.'
          );


          setSubmitting(
            false
          );

        }
      );
  }


  /*
   * Form submission.
   */
  document.addEventListener(
    'submit',
    submit,
    true
  );


  /*
   * Close buttons / background.
   */
  document.addEventListener(
    'click',
    function (event) {

      var closeButton =
        event.target.closest(
          '[data-saitech-quantity-request-close]'
        );


      var currentModal =
        event.target.closest(
          '[data-saitech-quantity-request-modal]'
        );


      if (closeButton) {

        event.preventDefault();

        close();

      } else if (
        currentModal &&
        event.target ===
          currentModal
      ) {

        close();

      }

    },
    true
  );


  /*
   * ESC key closes form.
   */
  document.addEventListener(
    'keydown',
    function (event) {

      var currentModal =
        modal();


      if (
        event.key ===
          'Escape' &&
        currentModal &&
        !currentModal.hidden
      ) {

        close();

      }

    }
  );


  /*
   * Public API used by
   * saitech-quantity-limit.js
   */
  api.open =
    open;

  api.close =
    close;


  window.SaitechQuantityRequestForm =
    api;

})();